/**
 * Shared utility toolkit
 * Consolidates small helpers into a single module to simplify imports.
 */

import { useEffect } from 'react';

const FALLBACK_PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'stackai-31d4e';
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

// Lazy accessor — avoids circular-dependency TDZ error with firebase.js
function getDefaultProjectId() {
  try {
    const { FIREBASE_PROJECT_ID } = require('../services/firebase');
    return FIREBASE_PROJECT_ID || FALLBACK_PROJECT_ID;
  } catch {
    return FALLBACK_PROJECT_ID;
  }
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

export function parseDate(dateValue) {
  if (!dateValue) return null;

  try {
    if (dateValue && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }

    if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
      return new Date(dateValue.seconds * 1000);
    }

    if (dateValue && typeof dateValue === 'object' && !('seconds' in dateValue)) {
      console.warn('Detected serverTimestamp placeholder - this should not happen with new data');
      return null;
    }

    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }

    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.error('Date parsing error:', error, dateValue);
    return null;
  }
}

export function formatDate(dateValue, options = {}) {
  const date = parseDate(dateValue);

  if (!date) {
    return options.fallback || '';
  }

  try {
    const defaultOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };

    return date.toLocaleDateString('en-US', {
      ...defaultOptions,
      ...options
    });
  } catch (error) {
    console.error('Date formatting error:', error);
    return options.fallback || 'Unknown date';
  }
}

export function getRelativeTime(dateValue) {
  const date = parseDate(dateValue);
  if (!date) return 'Unknown';

  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  }

  return formatDate(date);
}

export function isValidDate(dateValue) {
  return parseDate(dateValue) !== null;
}

// ---------------------------------------------------------------------------
// Dropdown helpers
// ---------------------------------------------------------------------------

export const createClickOutsideHandler = (dropdowns) => {
  return (event) => {
    dropdowns.forEach(({ isOpen, selector, onClose }) => {
      if (isOpen && !event.target.closest?.(selector)) {
        onClose();
      }
    });
  };
};

export const useClickOutside = (dropdowns) => {
  useEffect(() => {
    const handler = createClickOutsideHandler(dropdowns);
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [dropdowns.map((d) => d.isOpen).join(',')]);
};

// ---------------------------------------------------------------------------
// Environment helpers
// ---------------------------------------------------------------------------

const STRIPE_TEST_PUBLISHABLE_KEY = 'pk_test_51OzOr9GbI16EpXINiuOh0YDis8YMKFvIwNGQ78VAQI6ICZG7JFnHhOuLWkQo26zQP3KM9ui3Sfdeuv6N8DCEvjBD00fptSKLjx';
const STRIPE_LIVE_PUBLISHABLE_KEY = 'pk_live_51OzOr9GbI16EpXINhBv1iuHjZID78zsiOjhtdzLKacnqmCU87R7jtwpcEULRmcBT8So72LmofO0QVURUDjhLTBcs009KsNg6Av';

const sanitizeBaseUrl = (value) => {
  if (!value) return value;
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

export const isLocalEnvironment = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return LOCAL_HOSTNAMES.has(window.location.hostname);
};

export function getFunctionsBaseUrl(options = {}) {
  const { projectId: overrideProjectId, forceProduction = false } = options;

  // Some endpoints (e.g. the auth bridge) MUST hit the live deployed function
  // even from localhost — they verify Firebase ID tokens signed by the real
  // Firebase Auth backend, which the local emulator can't validate. Callers
  // pass forceProduction:true to bypass the emulator routing.
  if (forceProduction) {
    const projectId = overrideProjectId || getDefaultProjectId();
    return `https://us-central1-${projectId}.cloudfunctions.net`;
  }

  const projectId = overrideProjectId || getDefaultProjectId();
  const envBase = sanitizeBaseUrl(process.env.REACT_APP_FIREBASE_FUNCTIONS_URL);

  // XCUT-01: on localhost the emulator route WINS over any configured envBase.
  // A leftover `REACT_APP_FIREBASE_FUNCTIONS_URL=...cloudfunctions.net` (e.g. from
  // copying .env.example) must never make a dev machine hit LIVE Functions → LIVE
  // Stripe. If the env points at live cloudfunctions.net while we're local, ignore
  // it and use the emulator; honor envBase locally only when it is itself a
  // localhost URL (an explicit emulator override).
  if (isLocalEnvironment()) {
    const envIsLocal = envBase && /localhost|127\.0\.0\.1|0\.0\.0\.0/.test(envBase);
    if (envIsLocal) {
      return envBase;
    }
    if (envBase) {
      console.warn('[functions] Ignoring live REACT_APP_FIREBASE_FUNCTIONS_URL on localhost; routing to the emulator (XCUT-01).');
    }
    return `http://localhost:5001/${projectId}/us-central1`;
  }

  if (envBase) {
    return envBase;
  }

  return `https://us-central1-${projectId}.cloudfunctions.net`;
}

export function buildFunctionUrl(functionName, options = {}) {
  const base = getFunctionsBaseUrl(options);
  return `${base}/${functionName}`;
}

// The customer console (settings shell: organization admin, team, plan,
// account). On the main domain it's the /settings routes; once
// console.re-stack.com is connected (REACT_APP_CONSOLE_URL set), CTAs send
// users there instead. Same app, same paths, different host — no
// environment-specific logic beyond the env var.
export function buildConsoleUrl(path = '/settings') {
  const base = (process.env.REACT_APP_CONSOLE_URL || '').replace(/\/+$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

// True when the SPA is being served from the console subdomain.
export function isConsoleHost() {
  return typeof window !== 'undefined' && window.location.hostname.startsWith('console.');
}

export function getStripePublishableKey() {
  const envKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (envKey) {
    return envKey;
  }

  return isLocalEnvironment() ? STRIPE_TEST_PUBLISHABLE_KEY : STRIPE_LIVE_PUBLISHABLE_KEY;
}
